import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getOfferings, getEventTreatments, getEventLanes } from "@/lib/slotting/api";
import { seatingInit } from "@/lib/api/clients/SeatingClient";
import { getEmailTemplatesByEvent } from "@/lib/api/clients/EmailTemplateClient";
import { getEventCampaigns } from "@/lib/api/clients/EmailSentClient";
import { getGuestImports } from "@/lib/api/clients/GuestClient";

export type DynamicFieldOptions = {
  options: string[];
  optionLabels: Record<string, string>;
  /**
   * The source query has not resolved yet, so `options` is empty for a reason
   * that says nothing about the stored values. Consumers must not read an empty
   * catalog as "every reference is dead".
   */
  pending: boolean;
};

/**
 * Query key of the import-batch catalog (id → `name (guestCount)`), exported so
 * the post-import invalidation in BackgroundJobsProvider cannot drift away from
 * the declaration below. Two hand-written literals would silently unbind: the
 * invalidation would keep busting a key nobody reads and the "Import" column
 * would go back to rendering raw hex ids until a remount.
 */
export const importsFilterOptionsKey = (eventId: string | undefined) =>
  ["activity-filter-options", "imports", eventId] as const;

/**
 * Per-event option sources for the Tier-2 activity-rollup filter fields whose
 * values are ObjectIds shown by human name (apptOfferingIds → offerings,
 * seatRoomIds → rooms, emailedTemplateIds → email templates). Returns a map
 * keyed by FIELD NAME, each with `options` (the hex ids, stored as the filter
 * value) and `optionLabels` (id → display name).
 *
 * `enabled` gates the fetches so this is a no-op on pages whose field set has no
 * {@link Field.optionSource} fields (builders/settings) — the queries are
 * cached/deduped across the guest list + sendout audience that DO use it.
 */
export function useActivityFilterOptions(
  eventId: string | undefined,
  enabled: boolean,
): Record<string, DynamicFieldOptions> {
  // "0" is the sentinel id the create-event preview page mounts FieldsProvider
  // with — never a real event, so skip the fetches there.
  const on = !!eventId && eventId !== "0" && enabled;

  // staleTime 0 (+ default refetchOnMount) so the picker refreshes every time the
  // guest list / sendout audience remounts. Rooms/offerings/templates are created
  // on OTHER pages (seating, slotting, mail templates) that keep their own local
  // state and never touch this query cache, so a non-zero staleTime left the
  // filter showing a pre-mutation snapshot (e.g. a just-added room missing from
  // the "Raum" dropdown). Cached data still renders instantly; this only forces a
  // background re-fetch on mount. refetchOnWindowFocus:false (the global default
  // is true) keeps staleTime 0 from re-fetching on every tab refocus — a mount
  // refresh is enough and matches the event-shell query convention.
  const offeringsQ = useQuery({
    queryKey: ["activity-filter-options", "offerings", eventId],
    queryFn: () => getOfferings(eventId!),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const treatmentsQ = useQuery({
    queryKey: ["activity-filter-options", "treatments", eventId],
    queryFn: () => getEventTreatments(eventId!),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const lanesQ = useQuery({
    queryKey: ["activity-filter-options", "lanes", eventId],
    queryFn: () => getEventLanes(eventId!),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const roomsQ = useQuery({
    queryKey: ["activity-filter-options", "rooms", eventId],
    queryFn: () => seatingInit(eventId!),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const templatesQ = useQuery({
    queryKey: ["activity-filter-options", "templates", eventId],
    queryFn: () => getEmailTemplatesByEvent(eventId!, "emailTemplateId,templateName"),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const campaignsQ = useQuery({
    queryKey: ["activity-filter-options", "campaigns", eventId],
    queryFn: () => getEventCampaigns(eventId!),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const importsQ = useQuery({
    queryKey: importsFilterOptionsKey(eventId),
    queryFn: () => getGuestImports(eventId!),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const build = (
      pairs: Array<[string | undefined, string | undefined]>,
      pending: boolean,
    ): DynamicFieldOptions => {
      const options: string[] = [];
      const rawLabel: Record<string, string> = {};
      for (const [id, name] of pairs) {
        if (!id) continue;
        options.push(id);
        rawLabel[id] = name && name.trim() ? name : id;
      }
      // Disambiguate duplicate display names — two offerings/rooms/templates can
      // share a name (e.g. bulk-duplicated "X (copy)" renamed back, or two
      // "Lobby" rooms). The select cell reverses name→id by display string, so
      // identical labels would collapse to the first id (wrong/unfilterable
      // second item). Suffix a short id tail to make every label unique.
      const counts: Record<string, number> = {};
      for (const id of options) counts[rawLabel[id]] = (counts[rawLabel[id]] ?? 0) + 1;
      const optionLabels: Record<string, string> = {};
      for (const id of options) {
        const label = rawLabel[id];
        optionLabels[id] = counts[label] > 1 ? `${label} (${id.slice(-4)})` : label;
      }
      return { options, optionLabels, pending };
    };
    // `pending` is what keeps an empty catalog from being read as "every stored
    // reference is dead". These queries only ever run when `on` is true, and a
    // disabled query sits in `pending` status forever — so gate on `on` too,
    // otherwise every field on a builder/settings page would look mid-fetch.
    const isPending = (status: string) => on && status === "pending";
    return {
      apptOfferingIds: build(
        (offeringsQ.data ?? []).map((o) => [o.offeringId, o.name]),
        isPending(offeringsQ.status),
      ),
      apptTreatmentIds: build(
        (treatmentsQ.data ?? []).map((t) => [t.treatmentId, t.name]),
        isPending(treatmentsQ.status),
      ),
      apptLaneIds: build(
        (lanesQ.data ?? []).map((l) => [l.laneId, l.name]),
        isPending(lanesQ.status),
      ),
      seatRoomIds: build(
        (roomsQ.data?.rooms ?? []).map((r) => [r.roomId, r.name]),
        isPending(roomsQ.status),
      ),
      emailedTemplateIds: build(
        (templatesQ.data ?? []).map((t) => [t.emailTemplateId, t.templateName]),
        isPending(templatesQ.status),
      ),
      // Campaign labels are free text: the stored value IS the display name, so
      // pass [name, name]. distinct() already de-dups, so build's name-collision
      // suffix never fires.
      emailedSendNames: build(
        (campaignsQ.data ?? []).map((n) => [n, n]),
        isPending(campaignsQ.status),
      ),
      // CSV import batches: value = batch hex id, label = batch name + guest
      // count (e.g. "Import 01/07/2026, 13:52 (128)"). Names carry a timestamp so
      // they're already unique; build's collision suffix is a harmless backstop.
      // A batch can reach us WITHOUT a name: the queue consumer writes
      // `name: importName ?? null` and the AI import tool omits importName
      // entirely (PImport always sends one). Interpolating blind rendered the
      // literal "undefined (4)" / "null (4)" as the label — and because that
      // string is non-blank, build's own empty-name fallback below could never
      // catch it. Drop the name here instead so build falls back to the id.
      importId: build(
        (importsQ.data ?? []).map((b) => {
          const name = b.name?.trim();
          if (!name) return [b.importId, undefined];
          return [b.importId, b.guestCount != null ? `${name} (${b.guestCount})` : name];
        }),
        isPending(importsQ.status),
      ),
    };
  }, [
    on,
    offeringsQ.status,
    treatmentsQ.status,
    lanesQ.status,
    roomsQ.status,
    templatesQ.status,
    campaignsQ.status,
    importsQ.status,
    offeringsQ.data,
    treatmentsQ.data,
    lanesQ.data,
    roomsQ.data,
    templatesQ.data,
    campaignsQ.data,
    importsQ.data,
  ]);
}
